import React from "react";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import { trim } from "lodash";
import * as styles from "./styles.module.css";

/**
 * cardSingleTestimonial component.
 * Renders a single testimonial card with description, title, subtitle, media and background image.
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data for the card.
 * @returns {JSX.Element} The rendered component.
 */
const cardSingleTestimonial = ({ data }) => {
    // Destructure the data object
    const { cardItem, backgroundImage, additionalConfiguration, backgroundColor, customId } = data ?? {};
    const { description, title, subtitle, media, cta } = cardItem ?? {};

    // Get the padding class name based on the additional configuration
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration);
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    // Render the component
    return (
        <section className={`${sectionSpacingClassName} ${sectionColorMapping.bgColor}`} id={customId}>
            <div className="container">
                <div className="p-6.25 pt-12.5 lg:p-10 lg:pb-3.75 flex flex-col justify-between relative bg-blue rounded-sm lg:flex-row-reverse overflow-hidden">
                    {/* Check if backgroundImage is an image and render it */}
                    {backgroundImage?.length > 0 && backgroundImage?.[0]?.resource_type === "image" && (
                        <div className="absolute right-0 top-0 h-full">
                            <Media
                                key={backgroundImage?.[0]?.id}
                                link={backgroundImage?.[0]?.secure_url}
                                width={backgroundImage?.[0]?.width}
                                height={backgroundImage?.[0]?.height}
                                className="h-full object-cover"
                            />
                        </div>
                    )}
                    <div className="col flex justify-center items-center">
                        {/* Render the first media item if it exists */}
                        {media?.[0] && (
                            <div className="relative z-3 lg:left-15 lg:-top-[11px] xl:left-11.25">
                                <Media
                                    media={media?.[0]}
                                    className="w-[150px] h-[150px] sm:w-55 sm:h-55 xl:w-[335px] xl:h-[335px] object-contain rounded-full"
                                />
                            </div>
                        )}
                        {/* Render the second media item if it exists */}
                        {media?.[1] && (
                            <div>
                                <Media
                                    media={media?.[1]}
                                    className="absolute z-2 left-2/4 -translate-x-1/2 -top-1.5 sm:-top-7.5 lg:left-auto lg:-translate-x-0 lg:-translate-y-1/2 lg:-right-17.25 lg:top-1/2 xl:-right-[103px] object-contain w-65.25 h-[261px] sm:w-96 sm:h-96 xl:w-[560px] xl:h-[560px]"
                                />
                            </div>
                        )}
                    </div>
                    <div className="pt-14.25 lg:pt-10.5 lg:pb-6.75 lg:col-8">
                        <div className="max-w-[648px] lg:pr-10 xl:pr-0 flex flex-col gap-y-7.5 relative z-3 lg:h-full lg:justify-between">
                            {/* Render the description if it exists */}
                            {description?.description && (
                                <blockquote
                                    className={`text-md md:text-3xl relative leading-tight tracking-tight4 md:tracking-tight7 indent-[6px] md:indent-[8px] -mt-2.25  text-white font-semibold pb-0 ${styles.blockquote}`}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: renderSanitizedHtmlContent(trim(description?.description, [`"”`])),
                                    }}
                                />
                            )}
                            <div>
                                {(title || subtitle) && (
                                    <div
                                        className={`${
                                            cta ? "md:mb-7.5" : "md:mb-0"
                                        } flex flex-col mt-2.5 mb-2.5 md:mt-0`}
                                    >
                                        {/* Render the title if it exists */}
                                        {title && <div className="title-5 text-white leading-snug">{title}</div>}
                                        {/* Render the subtitle if it exists */}
                                        {subtitle && <div className="text-sm font-light text-white">{subtitle}</div>}
                                    </div>
                                )}
                                {cta && (
                                    <div className="flex gap-3.75 flex-wrap max-w-92.5 mt-5.25 md:max-w-125 lg:max-w-155">
                                        {cta?.map((button) => (
                                            <Cta key={button.id} data={button} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default cardSingleTestimonial;
